import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { PhotosService } from 'src/app/services/apps/photos/photos.service';
import { SharedModule } from '../shared.module';
import { FormsModule } from '@angular/forms';
import { Image } from 'src/app/services/apps/photos/photos.service';
import { MatDialog } from '@angular/material/dialog';
import { FileUploadComponent } from './file-upload.component';

interface entity {
  name: string;
  uuid: string;
}

interface FileImage {
  file?: File;
  type?: string;
  name: string;
  url: string;
  description?: string;
}

@Component({
  selector: 'app-carousel',
  standalone: true,
  imports: [
    TablerIconsModule,
    MaterialModule,
    CommonModule,
    SharedModule,
    FormsModule,
    SharedModule,
  ],
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
})
export class CarouselComponent {
  @Input() entity: entity;
  @Input() objectUuid?: string;
  @Input() photos?: string[];
  @Input() showDescriptionLabel = true;
  @Input() descriptionPlaceholder = '';

  showAlert = false;
  showCancel = true;
  showConfirm = true;
  alertMessage = '';

  description = '';

  index = 0;
  carouselUrls: FileImage[] = [];
  savedImages: Image[] = [];

  private alertResolve: ((value: boolean) => void) | null;

  constructor(
    private readonly photosService: PhotosService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.loadImages();
  }

  next() {
    if (this.carouselUrls.length === 0) return;
    this.index = (this.index + 1) % this.carouselUrls.length;
    this.description = this.carouselUrls[this.index]?.description || '';
  }

  prev() {
    if (this.carouselUrls.length === 0) return;
    this.index =
      (this.index - 1 + this.carouselUrls.length) % this.carouselUrls.length;
    this.description = this.carouselUrls[this.index]?.description || '';
  }

  private normalizeIndex() {
    if (this.index >= this.carouselUrls.length) {
      this.index = this.carouselUrls.length - 1;
    }
    if (this.index < 0) {
      this.index = 0;
    }
  }

  loadImages() {
    if (this.objectUuid) {
      this.photosService.list(this.objectUuid).subscribe({
        next: (images: Image[]) => {
          this.savedImages = images;
          this.carouselUrls = images.map((img) => ({
            name: img.url.split('/').pop() || '',
            url: img.url,
            description: img.description,
            type: 'SavedImage',
          }));
          this.normalizeIndex();
        },
      });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files || files.length === 0) return;
    this.processFiles(Array.from(files));
    input.value = '';
  }

  private processFiles(files: File[]) {
    const images = files.filter((file) => file.type.startsWith('image/'));
    if (!images.length) return;
    for (const file of images) {
      this.addTempImage(file);
    }
    this.index = this.carouselUrls.length - files.length;
    this.description = '';
  }

  private addTempImage(file: File) {
    const tempImage = {
      file: file,
      name: file.name,
      type: 'TempFile',
      description: this.description,
      url: URL.createObjectURL(file),
    };
    this.carouselUrls.push(tempImage);
    this.index = this.carouselUrls.length - 1;
  }

  openUploadModal() {
    const dialogRef = this.dialog.open(FileUploadComponent, {
      width: '700px',
      height: '400px',
      disableClose: false,
    });

    dialogRef.componentInstance.filesDropped.subscribe((files) => {
      this.processFiles(files);
      dialogRef.close();
    });

    dialogRef.componentInstance.filesSelected.subscribe((event) => {
      this.onFileSelected(event);
      dialogRef.close();
    });
  }

  addDescriptionToCurrentImage(value: string) {
    if (this.carouselUrls[this.index]) {
      this.carouselUrls[this.index].description = value;
    }
  }

  getImageUrlByCarouselUrls(index: number): string {
    return (
      this.carouselUrls[index]?.url ??
      '/assets/images/profinders/service_images_tip.png'
    );
  }

  async upload(uuid?: string) {
    if (uuid) {
      this.entity.uuid = uuid;
    }
    const payload = this.buildUploadPayload();
    this.sendUploadPayload(payload);
    this.clearTempUrls();
    this.loadImages();
  }

  private buildUploadPayload() {
    const payload = {
      files: this.getNewFiles(),
      entity: this.entity.name,
      objectUuid: this.entity.uuid,
      descriptions: this.getNewFilesDecriptions(),
    };
    return payload;
  }

  private sendUploadPayload(payload: any) {
    this.photosService.upload(payload).subscribe({
      next: () => {
        // console.log(`Fotos salvas com sucesso.`);
      },
      error: (err: HttpErrorResponse) => {
        console.error(`Erro ao salvar as fotos`, err);
      },
    });
  }

  private clearTempUrls() {
    this.carouselUrls = this.carouselUrls.filter(
      (item) => item.type !== 'TempFile',
    );
  }

  async delete(index: number) {
    const confirmed = await this.showConfirmationAlert(
      'Tem certeza que quer excluir a imagem?',
    );
    if (!confirmed) return;
    const file = this.carouselUrls[index];
    if (file.type === 'TempFile') {
      this.deleteTempFiles(index);
    } else if (file.type === 'SavedImage') {
      this.deleteSavedImage(file, index);
    }
  }

  private deleteTempFiles(index: number) {
    this.carouselUrls.splice(index, 1);
    this.normalizeIndex();
    this.loadImages();
  }

  private deleteSavedImage(file: FileImage, index: number) {
    this.photosService.delete(this.entity, file.url).subscribe({
      next: () => {
        this.carouselUrls.splice(index, 1);
        this.normalizeIndex();
      },
      error: (err: HttpErrorResponse) => {
        console.error(`Erro ao excluir a foto ${file.name}:`, err);
      },
    });
  }

  getNewFiles() {
    let newFiles: any = [];
    for (const item of this.carouselUrls) {
      if (item.type === 'TempFile') {
        newFiles.push(item.file);
      }
    }
    return newFiles;
  }

  getNewFilesDecriptions() {
    let descriptions: string[] = [];
    for (const item of this.carouselUrls) {
      if (item.type === 'TempFile') {
        descriptions.push(item.description || '');
      }
    }
    return descriptions;
  }

  getPayloadUpload() {
    return {
      files: this.getNewFiles(),
      entity: this.entity.name,
      objectUuid: this.entity.uuid,
      descriptions: this.getNewFilesDecriptions(),
    };
  }

  async showConfirmationAlert(message: string): Promise<boolean> {
    this.alertMessage = message;
    this.showAlert = true;
    this.showCancel = this.showConfirm = true;
    return new Promise<boolean>((resolve) => {
      this.alertResolve = resolve;
    });
  }

  onAlertClosed(confirmed: boolean) {
    this.showAlert = false;
    if (this.alertResolve) {
      this.alertResolve(confirmed);
      this.alertResolve = null;
    }
  }
}
